import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Award, AlertCircle, ChevronDown, ChevronRight, TrendingUp, School, User, FileText, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TeacherCard({ teacher, index, onSelect, isSelected }) {
  const totalPoints = teacher.merit_points - teacher.demerit_points;
  const maxPoints = Math.max(teacher.merit_points, teacher.demerit_points, 1);
  
  return (
    <Card 
      className={`paper-card cursor-pointer transition-all hover:shadow-lg ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onSelect(teacher.teacher_id)}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
              index === 0 ? 'bg-yellow-500' : 
              index === 1 ? 'bg-gray-400' : 
              index === 2 ? 'bg-amber-600' : 'bg-primary/60'
            }`}>
              {index + 1}
            </div>
            <div>
              <h3 className="font-semibold text-lg">{teacher.teacher_name}</h3>
              <div className="flex items-center space-x-4 mt-1 text-sm text-muted">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {teacher.total_records} жазба
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {teacher.students_served} оқушы
                </span>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="flex items-center space-x-4 mb-2">
              <div className="flex items-center gap-1">
                <Award className="w-4 h-4 text-green-600" />
                <span className="text-green-600 font-semibold">{teacher.merit_count}</span>
              </div>
              <div className="flex items-center gap-1">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-red-600 font-semibold">{teacher.demerit_count}</span>
              </div>
            </div>
            <div className={`text-xl font-bold ${totalPoints >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {totalPoints >= 0 ? '+' : ''}{totalPoints} ұпай
            </div>
          </div>
        </div>
        
        {/* Classes served */}
        {teacher.classes_served.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {teacher.classes_served.slice(0, 5).map((cls, i) => (
              <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                {cls}
              </span>
            ))}
            {teacher.classes_served.length > 5 && (
              <span className="text-xs text-muted">+{teacher.classes_served.length - 5}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TeacherDetailView({ teacherId, authHeaders }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedClasses, setExpandedClasses] = useState({});

  useEffect(() => {
    if (teacherId) {
      fetchDetail();
    }
  }, [teacherId]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/teacher-stats/${teacherId}`, {
        headers: authHeaders,
      });
      setDetail(response.data);
    } catch (error) {
      console.error('Error fetching teacher detail:', error);
      toast.error('Мәліметтерді жүктеу кезінде қате орын алды');
    } finally {
      setLoading(false);
    }
  };

  const toggleClass = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  if (loading) {
    return (
      <Card className="paper-card">
        <CardContent className="py-12 text-center text-muted">
          Жүктелуде...
        </CardContent>
      </Card>
    );
  }

  if (!detail) {
    return (
      <Card className="paper-card">
        <CardContent className="py-12 text-center text-muted">
          Мұғалімді таңдаңыз
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold heading-font">
        {detail.teacher_name} - Толық есеп
      </h3>
      <p className="text-sm text-muted">Ағымдағы ай: {detail.month}</p>

      {detail.classes.length === 0 ? (
        <Card className="paper-card">
          <CardContent className="py-12 text-center text-muted">
            Бұл мұғалім әлі ешкімге ұпай бермеген
          </CardContent>
        </Card>
      ) : (
        detail.classes.map((cls) => (
          <Card key={cls.class_id} className="paper-card">
            <Collapsible open={expandedClasses[cls.class_id]} onOpenChange={() => toggleClass(cls.class_id)}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedClasses[cls.class_id] ? (
                        <ChevronDown className="h-5 w-5 text-primary" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted" />
                      )}
                      <School className="h-5 w-5 text-primary" />
                      <div>
                        <CardTitle className="text-lg">{cls.class_name}</CardTitle>
                        <p className="text-sm text-muted">{cls.students.length} оқушы</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 px-4 pb-4">
                  <div className="space-y-3">
                    {cls.students.map((student) => (
                      <div key={student.student_id} className="border rounded-lg p-3 bg-white">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{student.student_name}</span>
                          <span className="text-sm text-muted">{student.records.length} жазба</span>
                        </div>
                        <div className="space-y-2">
                          {student.records.map((record, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 rounded p-2">
                              <div className="flex items-center gap-2">
                                {record.behavior_type === 'merit' ? (
                                  <Award className="w-4 h-4 text-green-600" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-red-600" />
                                )}
                                <span>{record.behavior_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={record.behavior_type === 'merit' ? 'text-green-600' : 'text-red-600'}>
                                  {record.behavior_type === 'merit' ? '+' : '-'}{record.points}
                                </span>
                                {record.created_at && (
                                  <span className="text-xs text-muted">
                                    {format(new Date(record.created_at), 'dd.MM HH:mm')}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {student.records.length > 0 && student.records[0].comment && (
                            <p className="text-xs text-muted italic mt-1">
                              "{student.records[0].comment}"
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))
      )}
    </div>
  );
}

export default function TeacherStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/teacher-stats`, {
        headers: getAuthHeaders(),
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching teacher stats:', error);
      toast.error('Мәліметтерді жүктеу кезінде қате орын алды');
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = {
      '01': 'Қаңтар', '02': 'Ақпан', '03': 'Наурыз', '04': 'Сәуір',
      '05': 'Мамыр', '06': 'Маусым', '07': 'Шілде', '08': 'Тамыз',
      '09': 'Қыркүйек', '10': 'Қазан', '11': 'Қараша', '12': 'Желтоқсан'
    };
    return `${months[month] || month} ${year}`;
  };

  if (loading) {
    return <div className="text-center py-12">Жүктелуде...</div>;
  }

  // Calculate totals
  const totalRecords = stats?.teachers?.reduce((sum, t) => sum + t.total_records, 0) || 0;
  const totalMerits = stats?.teachers?.reduce((sum, t) => sum + t.merit_count, 0) || 0;
  const totalDemerits = stats?.teachers?.reduce((sum, t) => sum + t.demerit_count, 0) || 0;
  const activeTeachers = stats?.teachers?.filter(t => t.total_records > 0).length || 0;

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports/teachers-pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mugalimder_esep_${new Date().toISOString().slice(0, 7)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF есебі жүктелді');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('PDF жүктеу кезінде қате орын алды');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="teacher-stats">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold heading-font tracking-tight flex items-center gap-2">
            <Users className="h-8 w-8 text-primary" />
            Мұғалімдер статистикасы
          </h2>
          <p className="text-muted">
            {stats?.month && formatMonth(stats.month)} - Мұғалімдер берген ұпайлар
          </p>
        </div>
        <Button onClick={downloadPDF} variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          PDF есебі
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold heading-font">{activeTeachers}</div>
            <p className="text-xs text-muted">Белсенді мұғалім</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold heading-font">{totalRecords}</div>
            <p className="text-xs text-muted">Жалпы жазба</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <Award className="w-6 h-6 text-green-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-green-600">{totalMerits}</div>
            <p className="text-xs text-muted">Мақтау</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-red-600">{totalDemerits}</div>
            <p className="text-xs text-muted">Сөгіс</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teachers List */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Мұғалімдер рейтингі</h3>
          
          {!stats?.teachers || stats.teachers.length === 0 ? (
            <Card className="paper-card">
              <CardContent className="py-12 text-center text-muted">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Бұл айда ешқандай жазба жоқ</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {stats.teachers
                .filter(t => t.total_records > 0)
                .map((teacher, index) => (
                  <TeacherCard
                    key={teacher.teacher_id}
                    teacher={teacher}
                    index={index}
                    onSelect={setSelectedTeacher}
                    isSelected={selectedTeacher === teacher.teacher_id}
                  />
                ))}
            </div>
          )}
        </div>

        {/* Teacher Detail */}
        <div>
          <h3 className="font-semibold text-lg mb-4">Толық мәліметтер</h3>
          {selectedTeacher ? (
            <TeacherDetailView 
              teacherId={selectedTeacher} 
              authHeaders={getAuthHeaders()}
            />
          ) : (
            <Card className="paper-card">
              <CardContent className="py-12 text-center text-muted">
                <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Мұғалімді таңдаңыз</p>
                <p className="text-sm mt-2">Сол жақтағы тізімнен мұғалімді таңдаңыз</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
